// Feature Requests page — MSP portal
// Users can submit feature/update requests and view the status of their org's submissions

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";

const API_URL = config.API_URL;

export default function FeatureRequests() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchRequests();
  }, []);

  async function fetchRequests() {
    try {
      const res = await axios.get(`${API_URL}/feature-requests`, authHeaders);
      setRequests(res.data);
    } catch (err) {
      console.error("Failed to load feature requests", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) {
      setErrorMsg("Please fill in both the title and description.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await axios.post(
        `${API_URL}/feature-requests?title=${encodeURIComponent(title)}&description=${encodeURIComponent(description)}`,
        {},
        authHeaders
      );
      setSuccessMsg("✅ Your request has been submitted successfully!");
      setTitle("");
      setDescription("");
      fetchRequests();
    } catch (err) {
      setErrorMsg("❌ Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function statusBadge(status) {
    if (status === "approved") return "bg-green-900 text-green-300";
    if (status === "closed") return "bg-gray-700 text-gray-400";
    return "bg-yellow-900 text-yellow-300";
  }

  function statusLabel(status) {
    if (status === "approved") return "✅ Approved";
    if (status === "closed") return "🔒 Closed";
    return "⏳ Pending";
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">Feature Requests</h1>
        <p className="text-gray-400 text-sm mb-8">
          Have an idea or need something improved? Submit a request and our team will review it.
        </p>

        {/* Submit form */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-10">
          <h2 className="text-lg font-semibold text-white mb-4">Submit a Request</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Short summary of your request"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe your request in detail - what problem does it solve, what would you like to see?"
              rows={5}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          {successMsg && <p className="text-green-400 text-sm mb-3">{successMsg}</p>}
          {errorMsg && <p className="text-red-400 text-sm mb-3">{errorMsg}</p>}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>

        {/* Existing requests */}
        <h2 className="text-lg font-semibold text-white mb-4">Your Organisation's Requests</h2>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : requests.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No requests submitted yet.
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(req => (
              <div key={req.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-sm mb-1">{req.title}</h3>
                    <p className="text-gray-400 text-sm whitespace-pre-wrap">{req.description}</p>
                    <p className="text-gray-600 text-xs mt-2">
                      Submitted by {req.submitted_by} · {new Date(req.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap ${statusBadge(req.status)}`}>
                    {statusLabel(req.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}